import type { AnnotationLabelT, AnnotationUnderUserReviewT, AnswerT, FrameRangeT, GenericAnnotationReviewDataT, RangeSpanT } from '../../../../common/types/types';
import type { RelationshipT } from '../../../../modernization-common/relationships';
import type { UndoRedoEvents } from '../../../../modernization-common/undo-redo/types';
import type { ImageSettingsT } from '../../../labelling/providers/stage-provider';
import type { PROPAGATION_METHOD } from '../../../labelling/reducers/actions/constants';
import type { FrameSpanT, LockAnnotationsPayloadT, ShowHideAnnotationsPayloadT } from '../../../labelling/reducers/actions/types';
import type { AnnotationT, BrushSettingsT, BulkAnnotationT, EditorModeT, FrameStateT, MetaDataT, SubToolT, ToolT } from '../../../labelling/types';

// Undoable events — annotation CRUD actions that participate in undo/redo.
// Added here as each annotation mutation is migrated to atom-based dispatch.
export const UNDOABLE_EVENTS = ({
  SAVE_RELATIONSHIPS: 'SAVE_RELATIONSHIPS',
  DELETE_RELATIONSHIP: 'DELETE_RELATIONSHIP',
  ADD_ANNOTATION: 'ADD_ANNOTATION',
  UPDATE_ANNOTATION: 'UPDATE_ANNOTATION',
  UPDATE_LABEL_FOR_ANNOTATION: 'UPDATE_LABEL_FOR_ANNOTATION',
  DELETE_ANNOTATION: 'DELETE_ANNOTATION',
  DELETE_ANNOTATIONS: 'DELETE_ANNOTATIONS',
  IMPORT_ANNOTATIONS: 'IMPORT_ANNOTATIONS',
  BULK_ADD_ANNOTATIONS: 'BULK_ADD_ANNOTATIONS',
  MERGE_POLYGON: 'MERGE_POLYGON',
  SUBTRACT_POLYGON: 'SUBTRACT_POLYGON',
  DELETE_SELECTED_ANNOTATIONS: 'DELETE_SELECTED_ANNOTATIONS',
  DELETE_ANNOTATION_FRAME_SPAN: 'DELETE_ANNOTATION_FRAME_SPAN',
  UPDATE_ALL_ANNOTATION_FRAME_SPAN: 'UPDATE_ALL_ANNOTATION_FRAME_SPAN',
  APPLY_FRAME_CLASSIFICATION_SPAN: 'APPLY_FRAME_CLASSIFICATION_SPAN',
  // Single dynamic attribute edits, scoped to one question across a frame span.
  UPDATE_ANNOTATION_ATTRIBUTE: 'UPDATE_ANNOTATION_ATTRIBUTE',
  DELETE_ANNOTATION_ATTRIBUTE: 'DELETE_ANNOTATION_ATTRIBUTE'
} as const);

// Non-undoable events — state changes that do not participate in undo/redo.
// Added here as each state variable is migrated from the legacy reducer.
export const EVENTS = ({
  SET_IMAGE_SETTINGS: 'SET_IMAGE_SETTINGS',
  SELECT_ANNOTATION: 'SELECT_ANNOTATION',
  FOCUS_ANNOTATION: 'FOCUS_ANNOTATION',
  TOGGLE_MULTI_SELECT: 'TOGGLE_MULTI_SELECT',
  SELECT_RELATIONSHIP_SOURCE: 'SELECT_RELATIONSHIP_SOURCE',
  SELECT_RELATIONSHIP_TARGET: 'SELECT_RELATIONSHIP_TARGET',
  SELECT_TOOL: 'SELECT_TOOL',
  SET_HOVERED_FRAME: 'SET_HOVERED_FRAME',
  SET_IMAGE_META_DATA: 'SET_IMAGE_META_DATA',
  TOGGLE_SNAP: 'TOGGLE_SNAP',
  TOGGLE_GRID_MODE: 'TOGGLE_GRID_MODE',
  SET_CURRENT_FRAME: 'SET_CURRENT_FRAME',
  SET_BRUSH_TOOL_SETTINGS: 'SET_BRUSH_TOOL_SETTINGS',
  HIDE_ANNOTATION_DETAILS_PANEL: 'HIDE_ANNOTATION_DETAILS_PANEL',
  OPEN_ANNOTATION_DETAILS_PANEL: 'OPEN_ANNOTATION_DETAILS_PANEL',
  SET_LOADING: 'SET_LOADING',
  UPDATE_ANNOTATION_PROPAGATION_METHOD: 'UPDATE_ANNOTATION_PROPAGATION_METHOD',
  RESET_ANNOTATION_LABEL: 'RESET_ANNOTATION_LABEL',
  COPY_ANNOTATION_ID: 'COPY_ANNOTATION_ID',
  SELECT_ANNOTATION_LABEL: 'SELECT_ANNOTATION_LABEL',
  TOGGLE_ANNOTATION_RELATIONSHIP_VIEW: 'TOGGLE_ANNOTATION_RELATIONSHIP_VIEW',
  CREATE_VIDEO_FRAMES: 'CREATE_VIDEO_FRAMES',
  SET_FRAME_COUNT: 'SET_FRAME_COUNT',
  UPDATE_ALL_ANNOTATION_LABELS: 'UPDATE_ALL_ANNOTATION_LABELS',
  UPDATE_ANNOTATION_LABELS: 'UPDATE_ANNOTATION_LABELS',
  // Annotation state mutations
  LOCK_ANNOTATIONS: 'LOCK_ANNOTATIONS',
  ADD_ANNOTATION_UNDER_REVIEW: 'ADD_ANNOTATION_UNDER_REVIEW',
  UPDATE_ANNOTATION_UNDER_REVIEW: 'UPDATE_ANNOTATION_UNDER_REVIEW',
  DELETE_ANNOTATION_UNDER_REVIEW: 'DELETE_ANNOTATION_UNDER_REVIEW',
  SHOW_HIDE_ANNOTATIONS: 'SHOW_HIDE_ANNOTATIONS',
  UPDATE_QUESTIONNAIRE: 'UPDATE_QUESTIONNAIRE',
  UPDATE_DYNAMIC_QUESTIONNAIRE: 'UPDATE_DYNAMIC_QUESTIONNAIRE',
  UPDATE_TRACKING_ANNOTATION_FRAMES: 'UPDATE_TRACKING_ANNOTATION_FRAMES',
  REMOVE_DYNAMIC_ANSWERS: 'REMOVE_DYNAMIC_ANSWERS',
  SET_FRAME_IMAGE_URL: 'SET_FRAME_IMAGE_URL'
} as const);
type SetImageSettingsEvent = {
  type: typeof EVENTS.SET_IMAGE_SETTINGS;
  imageSettings: ImageSettingsT;
};
type SelectAnnotationEvent = {
  type: typeof EVENTS.SELECT_ANNOTATION;
  annotationId: string | null | undefined;
};
type FocusAnnotationEvent = {
  type: typeof EVENTS.FOCUS_ANNOTATION;
  annotationId: string | null | undefined;
};
type ToggleMultiSelectEvent = {
  type: typeof EVENTS.TOGGLE_MULTI_SELECT;
  enabled: boolean;
};
type SelectRelationshipSourceEvent = {
  type: typeof EVENTS.SELECT_RELATIONSHIP_SOURCE;
  annotationId: string;
};
type SelectRelationshipTargetEvent = {
  type: typeof EVENTS.SELECT_RELATIONSHIP_TARGET;
  annotationId: string;
};
type SelectToolEvent = {
  type: typeof EVENTS.SELECT_TOOL;
  tool: ToolT;
  subTool?: SubToolT;
};
type SetHoveredFrameEvent = {
  type: typeof EVENTS.SET_HOVERED_FRAME;
  frameId: number | null;
};
type SetImageMetaDataEvent = {
  type: typeof EVENTS.SET_IMAGE_META_DATA;
  metaData: Partial<MetaDataT>;
};
type ToggleSnapEvent = {
  type: typeof EVENTS.TOGGLE_SNAP;
};
type ToggleGridModeEvent = {
  type: typeof EVENTS.TOGGLE_GRID_MODE;
};
type SetCurrentFrameEvent = {
  type: typeof EVENTS.SET_CURRENT_FRAME;
  frameId: number;
};
type SetBrushToolSettingsEvent = {
  type: typeof EVENTS.SET_BRUSH_TOOL_SETTINGS;
  brushToolSettings: BrushSettingsT;
};
type HideAnnotationDetailsPanelEvent = {
  type: typeof EVENTS.HIDE_ANNOTATION_DETAILS_PANEL;
};
type OpenAnnotationDetailsPanelEvent = {
  type: typeof EVENTS.OPEN_ANNOTATION_DETAILS_PANEL;
};
type SetLoadingEvent = {
  type: typeof EVENTS.SET_LOADING;
  isLoading: boolean;
};
type UpdateAnnotationPropagationMethodEvent = {
  type: typeof EVENTS.UPDATE_ANNOTATION_PROPAGATION_METHOD;
  propagationMethod: (typeof PROPAGATION_METHOD)[keyof typeof PROPAGATION_METHOD];
};
type ResetAnnotationLabelEvent = {
  type: typeof EVENTS.RESET_ANNOTATION_LABEL;
};
type CopyAnnotationIdEvent = {
  type: typeof EVENTS.COPY_ANNOTATION_ID;
  annotationId: string;
};
type SelectAnnotationLabelEvent = {
  type: typeof EVENTS.SELECT_ANNOTATION_LABEL;
  annotationLabel: AnnotationLabelT | null;
};
type ToggleAnnotationRelationshipViewEvent = {
  type: typeof EVENTS.TOGGLE_ANNOTATION_RELATIONSHIP_VIEW;
  editorMode: EditorModeT;
};
type CreateVideoFramesEvent = {
  type: typeof EVENTS.CREATE_VIDEO_FRAMES;
  frames: FrameStateT[];
};
type SetFrameCountEvent = {
  type: typeof EVENTS.SET_FRAME_COUNT;
  frameCount: number;
};
type UpdateAllAnnotationLabelsEvent = {
  type: typeof EVENTS.UPDATE_ALL_ANNOTATION_LABELS;
  annotationLabels: AnnotationLabelT[];
};
type UpdateAnnotationLabelsEvent = {
  type: typeof EVENTS.UPDATE_ANNOTATION_LABELS;
  color: string;
  title: string;
};
type LockAnnotationsEvent = {
  type: typeof EVENTS.LOCK_ANNOTATIONS;
} & LockAnnotationsPayloadT;
type AddAnnotationUnderReviewEvent = {
  type: typeof EVENTS.ADD_ANNOTATION_UNDER_REVIEW;
  annotations: Record<string, AnnotationUnderUserReviewT>;
};
type UpdateAnnotationUnderReviewEvent = {
  type: typeof EVENTS.UPDATE_ANNOTATION_UNDER_REVIEW;
  annotationId: string;
  annotation: Partial<AnnotationUnderUserReviewT>;
};
type DeleteAnnotationUnderReviewEvent = {
  type: typeof EVENTS.DELETE_ANNOTATION_UNDER_REVIEW;
  annotationIds: string[];
};
type ShowHideAnnotationsEvent = {
  type: typeof EVENTS.SHOW_HIDE_ANNOTATIONS;
} & ShowHideAnnotationsPayloadT;
type UpdateQuestionnaireEvent = {
  type: typeof EVENTS.UPDATE_QUESTIONNAIRE;
  annotationId: string;
  answers: AnswerT;
};
type UpdateDynamicQuestionnaireEvent = {
  type: typeof EVENTS.UPDATE_DYNAMIC_QUESTIONNAIRE;
  annotationId: string;
  answers: AnswerT;
  frameSpan: {
    start: number;
    end: number;
  };
};
type UpdateTrackingAnnotationFramesEvent = {
  type: typeof EVENTS.UPDATE_TRACKING_ANNOTATION_FRAMES;
  annotationIdx: string;
  startFrame: number;
  endFrame: number;
  frameAnnotations: Record<number, AnnotationT>;
};
type RemoveDynamicAnswersEvent = {
  type: typeof EVENTS.REMOVE_DYNAMIC_ANSWERS;
  annotationId: string;
  frameSpan: FrameSpanT;
};
type SetFrameImageUrlEvent = {
  type: typeof EVENTS.SET_FRAME_IMAGE_URL;
  frameIndex: number;
  imageUrl: string;
};
export type SaveRelationshipsEventPayload = {
  type: typeof UNDOABLE_EVENTS.SAVE_RELATIONSHIPS;
  relationships: RelationshipT[];
};
export type DeleteRelationshipEventPayload = {
  type: typeof UNDOABLE_EVENTS.DELETE_RELATIONSHIP;
  relationshipIds: string[];
  frameRange?: FrameRangeT;
};
export type UndoableRelationshipEventPayloads = SaveRelationshipsEventPayload | DeleteRelationshipEventPayload;
export type AddAnnotationEventPayload = {
  type: typeof UNDOABLE_EVENTS.ADD_ANNOTATION;
  frameIndex: number;
  annotationIdx: string;
  annotationData: AnnotationT;
  annotationReviewData?: GenericAnnotationReviewDataT;
};
export type UpdateAnnotationEventPayload = {
  type: typeof UNDOABLE_EVENTS.UPDATE_ANNOTATION;
  frameIndex: number;
  annotationIdx: string;
  annotationData: AnnotationT;
};
export type DeleteAnnotationEventPayload = {
  type: typeof UNDOABLE_EVENTS.DELETE_ANNOTATION;
  frameIndex: number;
  annotationIdx: string;
};
export type DeleteAnnotationsEventPayload = {
  type: typeof UNDOABLE_EVENTS.DELETE_ANNOTATIONS;
  currentFrame?: number;
  annotationLabels?: string[];
};
export type ImportAnnotationsEventPayload = {
  type: typeof UNDOABLE_EVENTS.IMPORT_ANNOTATIONS;
  frameIndex: number;
  annotationIdList: string[];
};
export type BulkAddAnnotationsEventPayload = {
  type: typeof UNDOABLE_EVENTS.BULK_ADD_ANNOTATIONS;
  annotations: BulkAnnotationT[];
};
export type MergePolygonEventPayload = {
  type: typeof UNDOABLE_EVENTS.MERGE_POLYGON;
};
export type SubtractPolygonEventPayload = {
  type: typeof UNDOABLE_EVENTS.SUBTRACT_POLYGON;
};
export type DeleteSelectedAnnotationsEventPayload = {
  type: typeof UNDOABLE_EVENTS.DELETE_SELECTED_ANNOTATIONS;
};
export type DeleteAnnotationFrameSpanEventPayload = {
  type: typeof UNDOABLE_EVENTS.DELETE_ANNOTATION_FRAME_SPAN;
  annotationIdx: string;
  frameSpan: RangeSpanT;
  preserveSelection?: boolean;
};
export type UpdateAllAnnotationFrameSpanEventPayload = {
  type: typeof UNDOABLE_EVENTS.UPDATE_ALL_ANNOTATION_FRAME_SPAN;
  annotationIdx: string;
  startFrame: number;
  endFrame: number;
};
export type UpdateLabelForAnnotationEventPayload = {
  type: typeof UNDOABLE_EVENTS.UPDATE_LABEL_FOR_ANNOTATION;
  annotationIdx: string;
  annotationLabel: AnnotationLabelT;
};
export type ApplyFrameClassificationSpanEventPayload = {
  type: typeof UNDOABLE_EVENTS.APPLY_FRAME_CLASSIFICATION_SPAN;
  annotationId: string;
  annotationLabel: AnnotationLabelT;
  answers: AnswerT;
  newSpan: RangeSpanT;
};
export type UpdateAnnotationAttributeEventPayload = {
  type: typeof UNDOABLE_EVENTS.UPDATE_ANNOTATION_ATTRIBUTE;
  annotationId: string;
  /** The one dynamic attribute (question) being edited; every sibling attribute is preserved. */
  questionId: string;
  /** The answer entry to write for `questionId` across the span. */
  answer: NonNullable<AnswerT[string]>;
  frameSpan: {
    start: number;
    end: number;
  };
};
export type DeleteAnnotationAttributeEventPayload = {
  type: typeof UNDOABLE_EVENTS.DELETE_ANNOTATION_ATTRIBUTE;
  annotationId: string;
  /** The one dynamic attribute (question) being removed; every sibling attribute is preserved. */
  questionId: string;
  frameSpan: {
    start: number;
    end: number;
  };
};
export type UndoableAnnotationEventPayloads = AddAnnotationEventPayload | UpdateAnnotationEventPayload | DeleteAnnotationEventPayload | DeleteAnnotationsEventPayload | ImportAnnotationsEventPayload | BulkAddAnnotationsEventPayload | MergePolygonEventPayload | SubtractPolygonEventPayload | DeleteSelectedAnnotationsEventPayload | DeleteAnnotationFrameSpanEventPayload | UpdateAllAnnotationFrameSpanEventPayload | UpdateLabelForAnnotationEventPayload | ApplyFrameClassificationSpanEventPayload | UpdateAnnotationAttributeEventPayload | DeleteAnnotationAttributeEventPayload;
export type UndoableEventPayloads = UndoableRelationshipEventPayloads | UndoableAnnotationEventPayloads;
export type StudioEvent = UndoRedoEvents | SetImageSettingsEvent | SelectAnnotationEvent | FocusAnnotationEvent | ToggleMultiSelectEvent | SelectRelationshipSourceEvent | SelectRelationshipTargetEvent | SelectToolEvent | SetHoveredFrameEvent | SetImageMetaDataEvent | ToggleSnapEvent | ToggleGridModeEvent | SetCurrentFrameEvent | SetBrushToolSettingsEvent | HideAnnotationDetailsPanelEvent | OpenAnnotationDetailsPanelEvent | SetLoadingEvent | UpdateAnnotationPropagationMethodEvent | ResetAnnotationLabelEvent | CopyAnnotationIdEvent | SelectAnnotationLabelEvent | ToggleAnnotationRelationshipViewEvent | CreateVideoFramesEvent | SetFrameCountEvent | UpdateAllAnnotationLabelsEvent | UpdateAnnotationLabelsEvent | LockAnnotationsEvent | AddAnnotationUnderReviewEvent | UpdateAnnotationUnderReviewEvent | DeleteAnnotationUnderReviewEvent | ShowHideAnnotationsEvent | UpdateQuestionnaireEvent | UpdateDynamicQuestionnaireEvent | UpdateTrackingAnnotationFramesEvent | RemoveDynamicAnswersEvent | SetFrameImageUrlEvent | UndoableEventPayloads;